import React from "react";
import { useFormikContext } from "formik";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Collapse,
  TextField,
  Autocomplete,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";

interface LeaseToFormProps {
  showForm: boolean;
  availableLessees: Array<{ id: number; name: string }>;
  entityType?: string; // "vehicle" or "trailer"
  TextFieldComponent?: React.ComponentType<any>; // Allow custom text field component
  DateFieldComponent?: React.ComponentType<any>; // Allow custom date field component
}

// Generic text field component
const GenericTextField: React.FC<{
  name: string;
  label: string;
  placeholder?: string;
  tooltip?: string;
}> = ({ name, label, placeholder, tooltip }) => {
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext<any>();
  
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  const value = getNestedValue(values, name) || '';
  const error = getNestedValue(errors, name);
  const isTouched = getNestedValue(touched, name);
  
  return (
    <TextField
      fullWidth
      type="date"
      name={name}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(error && isTouched)}
      helperText={error && isTouched ? safeErrorToString(error) : ''}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: tooltip && (
          <Tooltip title={tooltip} arrow>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      }}
    />
  );
};

// Generic lookup field for lessee selection
const GenericLookupField: React.FC<{
  name: string;
  label: string;
  options: Array<{ id: number; name: string }>;
  idFieldName: string;
  nameFieldName: string;
  placeholder?: string;
  tooltip?: string;
}> = ({ name, label, options, idFieldName, nameFieldName, placeholder, tooltip }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext<any>();
  
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const setNestedValue = (path: string, value: any) => {
    setFieldValue(path, value);
  };

  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  const currentValue = getNestedValue(values, nameFieldName) || '';
  const error = getNestedValue(errors, nameFieldName);
  const isTouched = getNestedValue(touched, nameFieldName);

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
      value={currentValue}
      onChange={(event, newValue) => {
        if (typeof newValue === 'object' && newValue !== null) {
          // Selected existing option - only send ID, clear name
          setNestedValue(idFieldName, newValue.id);
          setNestedValue(nameFieldName, null);
        } else {
          // Typed new value - only send name, clear ID
          setNestedValue(idFieldName, null);
          setNestedValue(nameFieldName, newValue || '');
        }
      }}
      onInputChange={(event, newInputValue) => {
        // Handle typing in the field
        setNestedValue(idFieldName, null);
        setNestedValue(nameFieldName, newInputValue || '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={Boolean(error && isTouched)}
          helperText={error && isTouched ? safeErrorToString(error) : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {tooltip && (
                  <Tooltip title={tooltip} arrow>
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const LeaseToForm: React.FC<LeaseToFormProps> = ({ 
  showForm, 
  availableLessees, 
  entityType = "asset",
  TextFieldComponent = GenericTextField,
  DateFieldComponent = GenericTextField
}) => {
  if (!showForm) {
    return null;
  }

  return (
    <Collapse in={showForm}>
      <Card sx={{ mt: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lease To Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Provide details about the company this {entityType} is leased to.
          </Typography>

          <Grid container spacing={2}>
            {/* Lessee Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Lessee Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <GenericLookupField
                name="lessee"
                label="Lessee Company"
                options={availableLessees}
                idFieldName="leaseToInfo.lesseeId"
                nameFieldName="leaseToInfo.lesseeName"
                placeholder="Type to search or add new lessee"
                tooltip="Select existing lessee or type to create new one"
              />
            </Grid>

            {/* Lease Period */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, mt: 2 }}>
                Lease Period
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateFieldComponent
                name="leaseToInfo.startDate"
                label="Lease Start Date"
                tooltip="When the lease period begins"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateFieldComponent
                name="leaseToInfo.endDate"
                label="Lease End Date"
                tooltip="When the lease period ends (optional)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  );
};

export default LeaseToForm;
